// shared/src/schemas/long-horizon-thesis.ts
// Zod schemas + row types for the Long-Horizon Thesis (LHT) tables.
// Migration-lockstep partner of migrations/0076_long_horizon_thesis.sql.
// docs/long-horizon-thesis-2026-07-09-brief-revision.md §3.6.

import { z } from 'zod';

// LhtStance (the 'uptrend'|'downtrend'|'range' union) is exported by the engine
// (shared/src/lht/state-machine.ts) — the canonical source. This Zod enum is the
// runtime validator for the same union; we don't re-export the type name to avoid
// an ambiguous double-export from the shared barrel.
export const LhtStanceEnum = z.enum(['uptrend', 'downtrend', 'range']);

export const LhtAssetClassEnum = z.enum(['equity', 'crypto', 'composite']);
export const LhtConfidenceEnum = z.enum(['low', 'med', 'high']);
export const LhtEventKindEnum = z.enum(['thesis_change', 'under_review_enter', 'under_review_exit']);

// One thesis pillar — a named load-bearing assumption + its falsifier. When a
// pillar carries a numeric price_level it is checkable in code (the tripwire);
// otherwise it's narrative until the phase-2 qualitative grader.
export const LhtPillarSchema = z.object({
  id:          z.string(),
  claim:       z.string(),
  falsifier:   z.string(),
  price_level: z.number().nullable().optional(),
  status:      z.enum(['intact', 'stressed', 'broken']).default('intact'),
});
export type LhtPillar = z.infer<typeof LhtPillarSchema>;

export const LhtKeyLevelsSchema = z.object({
  invalidation: z.number().nullable().optional(),
  reclaim:      z.number().nullable().optional(),
}).passthrough();

// ── lht_state row ──────────────────────────────────────────────────────────
export const LhtStateSchema = z.object({
  scope:            z.string(),
  asset_class:      LhtAssetClassEnum,
  version:          z.number().int(),
  stance:           LhtStanceEnum,
  since_date:       z.string(),                       // YYYY-MM-DD
  regime_label:     z.string(),
  thesis_text:      z.string(),
  pillars_json:     z.string(),                       // JSON array (parse → LhtPillar[])
  key_levels_json:  z.string(),                       // JSON object
  horizon_answer:   z.string(),
  gauge:            z.number().int().min(0).max(100),
  under_review:     z.union([z.literal(0), z.literal(1)]),
  t_stat:           z.number().nullable(),
  price:            z.number().nullable(),
  band_lower:       z.number().nullable(),
  band_upper:       z.number().nullable(),
  sma_long:         z.number().nullable(),
  sma_slope_pct:    z.number().nullable(),             // % change of the 200-SMA over smaSlopeLookback bars (rising>0/falling<0); null pre-SMA. Drives the display flip-edge direction (docs/lht-flip-visual-sma-anchor-2026-08-21-design.md)
  confidence:       LhtConfidenceEnum,
  llm_call_id:      z.string().nullable(),
  last_reviewed_at: z.string().nullable(),
  updated_at:       z.string(),                       // ISO-Z
  schema_version:   z.number().int(),
});
export type LhtStateRow = z.infer<typeof LhtStateSchema>;

// ── lht_revisions row ──────────────────────────────────────────────────────
export const LhtRevisionSchema = z.object({
  id:            z.string(),
  scope:         z.string(),
  version:       z.number().int(),
  from_stance:   LhtStanceEnum.nullable(),
  to_stance:     LhtStanceEnum,
  since_date:    z.string(),
  reason:        z.string(),
  evidence_json: z.string(),
  llm_call_id:   z.string().nullable(),
  created_at:    z.string(),
});
export type LhtRevisionRow = z.infer<typeof LhtRevisionSchema>;

// ── lht_daily row ──────────────────────────────────────────────────────────
export const LhtDailySchema = z.object({
  scope:        z.string(),
  date:         z.string(),                           // YYYY-MM-DD
  stance:       LhtStanceEnum,
  gauge:        z.number().int().min(0).max(100),
  t_stat:       z.number(),                           // composite MARKET: daily growth score
  under_review: z.union([z.literal(0), z.literal(1)]),
  aux_score:    z.number().nullable(),                // composite MARKET: daily inflation score; per-ticker: null
  created_at:   z.string(),
});
export type LhtDailyRow = z.infer<typeof LhtDailySchema>;

// ── lht_events row ─────────────────────────────────────────────────────────
export const LhtEventSchema = z.object({
  id:           z.string(),
  scope:        z.string(),
  version:      z.number().int().nullable(),
  kind:         LhtEventKindEnum,
  from_stance:  LhtStanceEnum.nullable(),
  to_stance:    LhtStanceEnum.nullable(),
  headline:     z.string(),
  acknowledged: z.union([z.literal(0), z.literal(1)]),
  created_at:   z.string(),
});
export type LhtEventRow = z.infer<typeof LhtEventSchema>;

/** Parse pillars_json → validated LhtPillar[] (empty array on malformed — the
 *  narrative pillars are display-only; a parse failure must not crash a read). */
export function parseLhtPillars(json: string): LhtPillar[] {
  try {
    const raw = JSON.parse(json);
    if (!Array.isArray(raw)) return [];
    return raw.map((p) => LhtPillarSchema.parse(p));
  } catch {
    return [];
  }
}

// ── Dashboard read-path (LhtBundle) ─────────────────────────────────────────
// The api-worker /api/lht reader parses the JSON columns server-side and returns
// these presentation-ready view types. Promoted to shared (C035): the web reads
// >3 server fields, so the contract lives here (server + client share it) rather
// than as a web-local widening cast. docs/long-horizon-thesis-2026-07-09-brief-revision.md §3.7.

/** One point on a scope's 90-day pressure/stance sparkline (from lht_daily). */
export interface LhtSparkPoint {
  date:      string;        // YYYY-MM-DD
  gauge:     number;        // 0–100
  stance:    'uptrend' | 'downtrend' | 'range';
  t_stat:    number;        // for composite MARKET this carries the growth score
  aux_score: number | null; // for composite MARKET this carries the inflation score (per-ticker: null)
}

/** A per-scope thesis view (SPY/$ETH narrated tickers + the deterministic legs).
 *  JSON columns already parsed: pillars + key_levels. */
export interface LhtScopeView {
  scope:            string;
  asset_class:      'equity' | 'crypto' | 'composite';
  stance:           'uptrend' | 'downtrend' | 'range';
  regime_label:     string;
  thesis_text:      string;
  horizon_answer:   string;
  since_date:       string;              // YYYY-MM-DD of the committed stance
  version:          number;
  gauge:            number;              // 0–100 pressure
  under_review:     0 | 1;
  confidence:       'low' | 'med' | 'high';
  t_stat:           number | null;
  price:            number | null;
  change_1d:        number | null;       // 1-day % change of the scope's price (from daily_closes; null if <2 closes)
  band_lower:       number | null;
  band_upper:       number | null;
  sma_long:         number | null;
  sma_slope_pct:    number | null;       // 200-SMA slope sign drives the flip-edge direction (uptrend-vs-range destination); null pre-SMA → flip 'pending'
  pillars:          LhtPillar[];         // parsed pillars_json
  key_levels:       { invalidation: number | null; reclaim: number | null };
  last_reviewed_at: string | null;
  updated_at:       string;
  sparkline:        LhtSparkPoint[];     // last 90 lht_daily points, chronological
}

/** The MARKET composite quadrant view. Extends the per-scope view with the
 *  growth×inflation payload decoded from key_levels_json (composite scope). */
export interface LhtQuadrantView extends LhtScopeView {
  quadrant:  string;                     // == regime_label (goldilocks/reflation/…)
  growth:    number;                     // signed composite score
  inflation: number;                     // signed composite score
  breadth:   number;                     // 0–1 fraction of legs trending
  legs:      Record<string, 'uptrend' | 'downtrend' | 'range'>;  // stocks/bonds/bondsInt/gold/oil/dollar
}

/** A change-feed event (banner). */
export interface LhtEventView {
  id:           string;
  scope:        string;
  version:      number | null;
  kind:         'thesis_change' | 'under_review_enter' | 'under_review_exit';
  from_stance:  ('uptrend' | 'downtrend' | 'range') | null;
  to_stance:    ('uptrend' | 'downtrend' | 'range') | null;
  headline:     string;
  acknowledged: 0 | 1;
  created_at:   string;
}

/** The full /api/lht bundle: the market quadrant, the narrated per-ticker theses,
 *  the deterministic cross-asset legs, and the recent change feed. */
export interface LhtBundle {
  market:     LhtQuadrantView | null;
  tickers:    LhtScopeView[];            // SPY, $ETH (narrated)
  legs:       LhtScopeView[];            // TLT/IEF/GLD/USO/DXY (deterministic-only)
  events:     LhtEventView[];            // most-recent first, capped
  updated_at: string | null;            // newest lht_state.updated_at across scopes
}
